import { useEffect, useState } from "react";
import { FileText, Save, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const L = {
  el: { title: "Εργασίες προς βαθμολόγηση", all: "Όλες", pending: "Προς βαθμολόγηση", graded: "Βαθμολογημένες",
    empty: "Δεν υπάρχουν εργασίες.", grade: "Βαθμός", feedback: "Σχόλια mentor", save: "Αποθήκευση & αποστολή",
    saved: "Βαθμολογήθηκε — στάλθηκε email στον μαθητή", module: "Module", view: "Προβολή εργασίας", hide: "Απόκρυψη" },
  en: { title: "Assignments to grade", all: "All", pending: "To grade", graded: "Graded",
    empty: "No assignments yet.", grade: "Grade", feedback: "Mentor feedback", save: "Save & send",
    saved: "Graded — email sent to the student", module: "Module", view: "View submission", hide: "Hide" },
};

export const AssignmentsAdmin = () => {
  const { lang } = useLang();
  const t = L[lang] || L.el;
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("submitted");
  const [open, setOpen] = useState("");
  const [form, setForm] = useState({});

  const reload = (status) => api.get(`/admin/assignments${status ? `?status=${status}` : ""}`)
    .then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { reload(filter); }, [filter]);

  const save = async (row) => {
    const payload = form[row.id] || { grade: row.grade ?? 0, feedback: row.feedback || "" };
    try {
      await api.put(`/admin/assignments/${row.id}`, { grade: Number(payload.grade), feedback: payload.feedback || "" });
      toast.success(t.saved);
      reload(filter);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const tab = (value, label) => (
    <button key={value} data-testid={`assignments-filter-${value || "all"}`} onClick={() => setFilter(value)}
      className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-colors duration-200 ${filter === value ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-transparent text-[#334155] border-[#E2E8F0]"}`}>
      {label}
    </button>
  );

  return (
    <div className="mt-10" data-testid="admin-assignments-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-heading font-black text-2xl text-[#0B1B3A] flex items-center gap-2"><FileText className="w-5 h-5 text-[#1D4ED8]" /> {t.title}</h2>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#64748B]" />
          {tab("submitted", t.pending)}
          {tab("graded", t.graded)}
          {tab("", t.all)}
        </div>
      </div>

      {rows === null ? <p className="text-[#64748B] mt-6">…</p> : rows.length === 0 ? (
        <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-assignments-empty">{t.empty}</div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-assignments-list">
          {rows.map((row) => {
            const f = form[row.id] || { grade: row.grade ?? "", feedback: row.feedback || "" };
            return (
              <div key={row.id} className="card-dark p-5" data-testid={`admin-assignment-${row.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <p className="font-bold text-[#0B1B3A]">{row.user_name || row.user_email} <span className="text-xs text-[#64748B]">· {row.user_email}</span></p>
                    <p className="text-xs text-[#64748B] mt-1">{row.course_title} · {t.module} {row.module} · {(row.submitted_at || "").slice(0, 16).replace("T", " ")}</p>
                    <p className="text-xs text-[#94A3B8] mt-1">{row.case_title}</p>
                  </div>
                  <span className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${row.status === "graded" ? "bg-[#1D4ED8] text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
                    {row.status === "graded" ? `${Math.round(row.grade)}/100` : t.pending}
                  </span>
                  <button data-testid={`admin-assignment-view-${row.id}`} onClick={() => setOpen(open === row.id ? "" : row.id)}
                    className="px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border border-[#E2E8F0] text-[#334155] hover:border-[#1D4ED8]">
                    {open === row.id ? t.hide : t.view}
                  </button>
                </div>

                {open === row.id && (
                  <>
                    <div className="mt-4 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4 max-h-80 overflow-auto" data-testid={`admin-assignment-text-${row.id}`}>
                      <p className="text-sm text-[#334155] whitespace-pre-line">{row.text}</p>
                    </div>
                    <div className="mt-4 flex flex-wrap items-end gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B] mb-1">{t.grade}</p>
                        <input data-testid={`admin-assignment-grade-${row.id}`} type="number" min={0} max={100} value={f.grade}
                          onChange={(e) => setForm({ ...form, [row.id]: { ...f, grade: e.target.value } })} className="input-cream w-24" />
                      </div>
                      <div className="flex-1 min-w-[240px]">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B] mb-1">{t.feedback}</p>
                        <textarea data-testid={`admin-assignment-feedback-${row.id}`} rows={3} value={f.feedback}
                          onChange={(e) => setForm({ ...form, [row.id]: { ...f, feedback: e.target.value } })} className="input-cream w-full" />
                      </div>
                      <button data-testid={`admin-assignment-save-${row.id}`} onClick={() => save(row)} className="btn-blue inline-flex items-center gap-2"><Save className="w-4 h-4" /> {t.save}</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignmentsAdmin;
